import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { 
  FileText, 
  Package, 
  TrendingUp, 
  Calendar,
  Download,
  Eye,
  MoreHorizontal
} from "lucide-react";
import { ClipboardList } from "lucide-react";
import type { NavigationItem } from "../Navigation";

interface ProductDashboardProps {
  onNavigateToFeature: (feature: NavigationItem) => void;
}

export function ProductDashboard({ onNavigateToFeature }: ProductDashboardProps) {
  const stats = [
    {
      title: "Total PDF Jobs",
      value: "156",
      change: "+12%",
      trend: "up",
      icon: FileText
    },
    {
      title: "Active Templates",
      value: "8",
      change: "+2",
      trend: "up",
      icon: Package
    },
    {
      title: "Monthly Generation",
      value: "2,847",
      change: "+23%",
      trend: "up",
      icon: TrendingUp
    },
    {
      title: "Success Rate",
      value: "98.2%",
      change: "+1.1%",
      trend: "up",
      icon: Calendar
    }
  ];

  const recentJobs = [
    {
      id: "1",
      name: "Product Catalog Q1 2024",
      template: "Product Catalog",
      status: "completed" as const,
      files: 156,
      date: "2024-01-16T10:30:00"
    },
    {
      id: "2",
      name: "Customer Reports - December",
      template: "Customer Report",
      status: "processing" as const,
      files: 89,
      progress: 67,
      date: "2024-01-16T09:00:00"
    },
    {
      id: "3",
      name: "Certificate Batch #240115",
      template: "Certificate",
      status: "completed" as const,
      files: 245,
      date: "2024-01-15T16:45:00"
    },
    {
      id: "4",
      name: "Invoice Generation - Week 3",
      template: "Invoice",
      status: "failed" as const,
      files: 0,
      date: "2024-01-15T14:20:00"
    }
  ];

  const quickActions = [
    {
      title: "Business Rules",
      description: "Manage product quality validation rules",
      icon: ClipboardList,
      action: () => onNavigateToFeature("business-rule-management"),
      color: "bg-violet-500"
    },
    {
      title: "Bulk PDF Generation",
      description: "Generate multiple PDFs from templates",
      icon: FileText,
      action: () => onNavigateToFeature("bulk-pdf-generation"),
      color: "bg-blue-500"
    }
  ];

  const getStatusBadge = (status: 'completed' | 'processing' | 'failed') => {
    const variants = {
      completed: { variant: "default" as const, color: "text-green-600" },
      processing: { variant: "secondary" as const, color: "text-blue-600" },
      failed: { variant: "destructive" as const, color: "text-red-600" }
    };
    
    return (
      <Badge variant={variants[status].variant} className="capitalize">
        {status}
      </Badge>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Product Management</h1>
          <p className="text-muted-foreground">Manage product-related workflows and generation tools</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.title}
                </CardTitle>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground">
                  <span className={stat.trend === 'up' ? 'text-green-600' : 'text-red-600'}>
                    {stat.change}
                  </span>
                  {' '}from last month
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>
                Access frequently used features
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {quickActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                    onClick={action.action}
                  >
                    <div className={`${action.color} p-2 rounded-md text-white`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium">{action.title}</h3>
                      <p className="text-sm text-muted-foreground">{action.description}</p>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Recent Jobs */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Recent Jobs</CardTitle>
                <CardDescription>
                  Latest PDF generation activities
                </CardDescription>
              </div>
              <Button variant="outline" size="sm">
                View All
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentJobs.map((job) => (
                  <div key={job.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium">{job.name}</h3>
                        {getStatusBadge(job.status)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Template: {job.template} � {job.files} files
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(job.date).toLocaleString()}
                      </div>
                      {job.status === 'processing' && job.progress && (
                        <div className="mt-2">
                          <Progress value={job.progress} className="h-1" />
                          <div className="text-xs text-muted-foreground mt-1">
                            {job.progress}% complete
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {job.status === 'completed' && (
                        <>
                          <Button size="sm" variant="outline">
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="outline">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="outline">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}